import {Injectable} from '@angular/core';
import {PointsService} from "../points.service";
import {FeatureType} from "../../models/EnumTypes";
import {Project} from "../../models/project";
import {GritInterferenceTestService} from "./grit.interference-test.service";
import {FeatureCollection} from "geojson";
import {GritFilter} from "../../models/grit/grit.filter";
import {icaoccModel} from "../../models/icaoccModel";
import {HttpClient} from "@angular/common/http";
import {EndpointsService} from "../endpoints.service";
import {ShortcutsModalControlService} from "../../popup/configurations/services/shortcuts-modal-control.service";
import {GritRadarService} from "./grit.radar.service";
import {Geometry} from "ol/geom";
import {UiBlockService} from "../ui-block.service";
import {WKT} from "ol/format";
import {CoordenateService} from "../coordenate.service";
import {GritOption} from "../../models/grit/grit.model";
import {GritUserService} from "./grit.user.service";
import {ModalControlService} from "../modal-control.service";


@Injectable({
  providedIn: 'root'
})
export class GritProjectService {

  initial : boolean = true;

  static globalInformation = {
    geoserverUrl: 'https://docker11723-dev-demeter.jelastic.labs.gmv.com/geoserver/DEMETER/ows?',
    //FIRS Map. Key is code and value is name
    firs: [] as GritOption<string>[],
    //Countries Map. Key is ICCAO and value is name
    countries : [] as GritOption<string>[],

    boolean : [{display: "True", value: "true"}, {display: "False", value: "false"}] as GritOption<string>[],

    format : new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
  }

  constructor(
    // private userService: GritUserService,

    private pointsService : PointsService,
    private interferenceTestService : GritInterferenceTestService,
    private radarService : GritRadarService,
    private http: HttpClient,
    private endpointService : EndpointsService,
    private shortcutsService : ShortcutsModalControlService,
    private uiBlock : UiBlockService,
    private rcsControl: CoordenateService,
    protected modalService: ModalControlService,


  ) { }

  //Setup information needed across projects
  private async initializeProjects(){
    //Get FIRS
    const url = (
      GritProjectService.globalInformation.geoserverUrl +
      new URLSearchParams({'service': 'WFS', 'version':'1.0.0',
        'request': 'GetFeature','typeName': 'DEMETER:ECAC_FIRUIR_Lower',
        'maxFeatures':'300', 'outputFormat':'application/json'} ).toString()
    );
    let geoServerFeatures = JSON.parse(await fetch(url).then((response) => response.text())) as FeatureCollection

    geoServerFeatures.features.forEach(feature => {
      GritProjectService.globalInformation.firs.push({
        value: feature.properties.IDENT_TXT,
        display: `${feature.properties.NAME_TXT} (${feature.properties.IDENT_TXT})`
      })
    })
    GritProjectService.globalInformation.firs.sort((a, b) => a.display.localeCompare(b.display))

    //Get Countries
    this.http.get<icaoccModel[]>(
      this.endpointService.icaoccUrl,
      this.endpointService.httpOptions
    ).subscribe((icaocodes) => {
      let firIcaoCodes = GritProjectService.globalInformation.firs.map(go => go.value.toString().slice(0,2))

       icaocodes.forEach(item => {
         if (firIcaoCodes.includes(item.icaocode)){
           GritProjectService.globalInformation.countries.push({
             value: item.icaocode,
             display: `${item.countryname} (${item.icaocode})`
           })
         }
       });

      GritProjectService.globalInformation.countries.sort((a, b) => a.display.localeCompare(b.display))
    });

    this.initial = false
  }

  clean(){
    this.pointsService.clearGritFeaturesFromLayer(FeatureType.InterferenceTest);
    this.pointsService.clearGritFeaturesFromLayer(FeatureType.Device);
    this.pointsService.clearGritFeaturesFromLayer(FeatureType.Receiver);
    this.pointsService.clearGritFeaturesFromLayer(FeatureType.Radar);
  }

  get(project: Project){
    this.interferenceTestService.activateProject(project.id == 1);
    this.pointsService.fetchAndAddInterferenceTest();

    this.radarService.activateProject(project.id == 1);
    this.pointsService.fetchAndAddRadars();

    if (project.id == 1) return;

    this.pointsService.fetchAndAddDevices();
    // this.pointsService.fetchAndAddReceivers();
  }

  async getFir(geometry: Geometry) : Promise<string[]>{
    this.uiBlock.startBlock("Getting Firs...")

    try{
      let wkt = new WKT().writeGeometry(geometry, {
        dataProjection: 'CRS:84',
        featureProjection: this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : "EPSG:3857"
      })

      const url = (
        'https://docker11723-dev-demeter.jelastic.labs.gmv.com/geoserver/DEMETER/ows?' +
        new URLSearchParams({'service': 'WFS', 'version':'1.0.0',
          'request': 'GetFeature','typeName': 'DEMETER:ECAC_FIRUIR_Lower',
          'CQL_FILTER': 'INTERSECTS(the_geom, '+ wkt + ')',
          'maxFeatures':'300', 'outputFormat':'application/json'} ).toString()
      );

      let geoServerFeatures = JSON.parse(await fetch(url).then((response) => response.text())) as FeatureCollection

      this.uiBlock.stopBlock();
      return geoServerFeatures.features.map(f => f.properties.IDENT_TXT) as string[];
    } catch(error) {
      this.uiBlock.stopBlock();
      console.error(error)
    }
  }

  switchProject(project : Project = null){
    try{
      if (this.initial) this.initializeProjects()

      this.clean();

      this.get(project);

      this.shortcutsService.updateShortcutsList();

    } catch (error) {
      this.uiBlock.stopBlock();
      this.modalService.showMesssageModal("Error Loading Project", `Error:\n ${error}`);
    }
  }
}
